import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { Building2, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { TemplateSelector, TemplateIcon } from './templates';
import { projectId } from '../utils/supabase/info';
import type { TemplateType } from '../types';
import { ForgeIntegrationSettings } from './ForgeIntegrationSettings';

interface QuickCompanySettingsDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  accessToken: string;
  onUpdated: () => void;
}

export function QuickCompanySettingsDialog({
  open,
  onClose,
  companyId,
  companyName,
  accessToken,
  onUpdated
}: QuickCompanySettingsDialogProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Template state
  const [currentTemplate, setCurrentTemplate] = useState<TemplateType>('custom');
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('custom');
  const [editingTemplate, setEditingTemplate] = useState(false);
  
  // Company status state
  const [companyActive, setCompanyActive] = useState(true);
  const [originalActive, setOriginalActive] = useState(true);

  useEffect(() => {
    if (open) {
      loadCompanySettings();
    } else {
      // Reset state on close
      setError('');
      setSuccess('');
      setEditingTemplate(false);
    }
  }, [open, companyId]);

  const loadCompanySettings = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Load template
      const templateResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/template`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (templateResponse.ok) {
        const templateData = await templateResponse.json();
        const template = templateData.template || 'custom';
        setCurrentTemplate(template);
        setSelectedTemplate(template);
      }

      // Load company details for active status
      // Use consultant/companies endpoint which is accessible to CF Admins
      const companiesResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/companies`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (companiesResponse.ok) {
        // GET /consultant/companies answers { companies: [...] }; older handlers answered a bare list
        const data = await companiesResponse.json();
        const companies: any[] = Array.isArray(data) ? data : data?.companies || [];
        const currentCompany = companies.find((c: any) => c.id === companyId);
        if (currentCompany) {
          const isActive = currentCompany.status !== 'inactive';
          setCompanyActive(isActive);
          setOriginalActive(isActive);
        }
      }
    } catch (err: any) {
      console.error('Error loading company settings:', err);
      setError(err.message || 'Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (selectedTemplate === currentTemplate) {
      setEditingTemplate(false);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/template`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ template: selectedTemplate }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update template');
      }

      setCurrentTemplate(selectedTemplate);
      setEditingTemplate(false);
      setSuccess('Template updated successfully');
      onUpdated();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error updating template:', err);
      setError(err.message || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCompanyStatus = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    const newStatus = companyActive ? 'inactive' : 'active';

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/company/${companyId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update company status');
      }

      setCompanyActive(!companyActive);
      setOriginalActive(!companyActive);
      setSuccess(`Company ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      onUpdated();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error updating company status:', err);
      setError(err.message || 'Failed to update company status');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = selectedTemplate !== currentTemplate || companyActive !== originalActive;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Company Settings: {companyName}
          </DialogTitle>
          <DialogDescription>
            Manage template and activation status for this company
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">Loading settings...</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[calc(90vh-200px)] pr-4">
            <div className="space-y-6 py-4">
              {/* Success Message */}
              {success && (
                <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <AlertDescription className="text-green-800 dark:text-green-200">
                    {success}
                  </AlertDescription>
                </Alert>
              )}

              {/* Error Message */}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Company Status */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Company Status</Label>
                    <p className="text-sm text-muted-foreground">
                      {companyActive ? 'Company is active' : 'Company is deactivated'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={companyActive ? 'default' : 'secondary'}>
                      {companyActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Switch
                      checked={companyActive}
                      onCheckedChange={setCompanyActive}
                      disabled={saving}
                    />
                  </div>
                </div>
                {!companyActive && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Inactive companies cannot access the platform. Users will not be able to log in.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* ITSM Platform Template */}
              <div className="space-y-3">
                <Label>ITSM Platform Template</Label>
                <p className="text-sm text-muted-foreground">
                  The platform template determines available features and export formats
                </p>

                {!editingTemplate ? (
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <TemplateIcon template={currentTemplate} className="h-6 w-6" />
                      <div>
                        <p className="font-medium">
                          {currentTemplate.charAt(0).toUpperCase() + currentTemplate.slice(1)} Template
                        </p>
                        <p className="text-sm text-muted-foreground">Current platform configuration</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingTemplate(true)}
                      disabled={saving}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <TemplateSelector 
                      selectedTemplate={selectedTemplate}
                      onSelect={setSelectedTemplate}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleUpdateTemplate}
                        disabled={saving || selectedTemplate === currentTemplate}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Saving...
                          </>
                        ) : (
                          'Save Template'
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedTemplate(currentTemplate);
                          setEditingTemplate(false);
                        }}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Atlassian Forge Integration - Only show for Atlassian template */}
              {currentTemplate === 'atlassian' && (
                <div className="space-y-3">
                  <ForgeIntegrationSettings
                    accessToken={accessToken}
                    companyId={companyId}
                    companyName={companyName}
                  />
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          {companyActive !== originalActive && (
            <Button
              onClick={handleToggleCompanyStatus}
              disabled={saving || loading}
              variant={companyActive ? 'destructive' : 'default'}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : companyActive ? (
                'Deactivate Company'
              ) : (
                'Activate Company'
              )}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}